/**
 * Turns an original image path into the responsive set built by
 * `npm run images`.
 *
 * Call sites keep naming the original — "/assets/images/homepage.jpg" — and
 * this resolves it against src/config/imageManifest.json. Nothing in the app
 * references a generated filename, so regenerating at different widths or in a
 * new format needs no component changes, and an asset that is not in the
 * manifest degrades to the original rather than 404ing.
 */
import manifest from "../config/imageManifest.json";

/** Widest CSS pixels each `sizes` keyword resolves to, for the `x` fallback. */
const DEFAULT_SIZES = "100vw";

export function imageEntry(src) {
  if (!src) return null;
  // Tolerate callers that pass a bare filename or a relative path.
  const key = src.startsWith("/assets/images/")
    ? src
    : `/assets/images/${src.replace(/^\.?\/?(assets\/images\/)?/, "")}`;
  return manifest[key] ?? null;
}

/** "url 1920w, url 3840w" for one format. */
function srcSet(list) {
  return list.map(({ url, width }) => `${url} ${width}w`).join(", ");
}

/**
 * Everything a <picture> needs for `src`.
 *
 * Returns null when the asset has no derivatives, which lets a caller fall
 * straight back to a plain <img src={src}>.
 */
export function pictureSources(src, sizes = DEFAULT_SIZES) {
  const entry = imageEntry(src);
  if (!entry) return null;

  return {
    // AVIF first: the browser takes the first <source> it understands.
    sources: ["avif", "webp"]
      .filter((format) => entry.sources[format]?.length)
      .map((format) => ({
        type: `image/${format}`,
        srcSet: srcSet(entry.sources[format]),
        sizes,
      })),
    fallback: entry.fallback ?? src,
    width: entry.width,
    height: entry.height,
    aspectRatio: entry.aspectRatio,
  };
}

/**
 * An sx fragment that paints `src` as a background at the density the display
 * actually has. Spread it into the element's sx:
 *
 *   sx={{ ...backgroundImageSx("/assets/images/car-bg.jpg"), backgroundSize: "cover" }}
 *
 * This returns a fragment rather than a bare `background-image` value because
 * MUI reads an array sx value as *responsive breakpoints*, not as the repeated
 * declarations CSS uses for fallback — handing it ["url(...)", "image-set(...)"]
 * quietly produces "jpg below 600px, image-set above", which is not a fallback
 * at all. @supports states the same intent in a way MUI cannot misread.
 *
 * Three tiers, each genuinely gated:
 *   1. a plain url(), which every engine understands;
 *   2. a WebP image-set() for anything that can pick by density;
 *   3. an AVIF-first typed image-set() where type() negotiation exists, so a
 *      browser that parses the syntax but cannot decode AVIF drops to the WebP
 *      candidate inside the same image-set instead of painting nothing.
 *
 * `baseWidth` is the width treated as 1x — for a full-bleed hero that is the
 * widest viewport we design for, so a 2x phone does not download the 4K file
 * it has no use for.
 */
export function backgroundImageSx(src, { baseWidth = 1920 } = {}) {
  const entry = imageEntry(src);
  if (!entry) return { backgroundImage: `url('${src}')` };

  const fallback = entry.fallback ?? src;
  const sx = { backgroundImage: `url('${fallback}')` };

  // Pick the narrowest file that still covers each density, so the 1x slot is
  // not silently handed a 4K image.
  const pick = (list, density) =>
    list.find(({ width }) => width >= baseWidth * density)?.url ??
    list[list.length - 1]?.url;

  const candidates = (format) => {
    const list = entry.sources[format] ?? [];
    if (!list.length) return null;
    const one = pick(list, 1);
    const two = pick(list, 2);
    return { one, two, single: one === two };
  };

  const webp = candidates("webp");
  if (webp) {
    sx["@supports (background-image: image-set(url('i.webp') 1x))"] = {
      backgroundImage: webp.single
        ? `image-set(url('${webp.one}') 1x)`
        : `image-set(url('${webp.one}') 1x, url('${webp.two}') 2x)`,
    };
  }

  const avif = candidates("avif");
  if (avif && webp) {
    const typed = (url, format, density) =>
      `url('${url}') type("image/${format}") ${density}x`;
    const set = [
      typed(avif.one, "avif", 1),
      ...(avif.single ? [] : [typed(avif.two, "avif", 2)]),
      typed(webp.one, "webp", 1),
      ...(webp.single ? [] : [typed(webp.two, "webp", 2)]),
    ].join(", ");
    sx[`@supports (background-image: image-set(url('i.avif') type("image/avif") 1x))`] = {
      backgroundImage: `image-set(${set})`,
    };
  }

  return sx;
}

/**
 * `sizes` strings for the layouts this app repeats. Getting these right is
 * what stops a phone downloading the desktop file: without `sizes`, the
 * browser assumes 100vw and over-fetches on every small element.
 */
export const SIZES = {
  fullBleed: "100vw",
  // "How It Works" strips: 80vw on phones, 60vw from md up.
  diagram: "(max-width: 900px) 80vw, 60vw",
  // Section illustrations: 60vw on phones, 25-30vw from md up.
  illustration: "(max-width: 900px) 60vw, 30vw",
  // Product tiles: full width on phones, then the image column of a half-width card.
  cardTile: "(max-width: 900px) 40vw, 20vw",
  // Journey option cards in a 2-3 across grid.
  choice: "(max-width: 600px) 90vw, (max-width: 1200px) 45vw, 380px",
  // Benefit-grid icons: measured at 197px inside a 333px card at desktop.
  // This has to be at least the painted size or the browser picks a smaller
  // file and enlarges it, which is the pixellation this whole change exists
  // to remove.
  icon: "(max-width: 900px) 30vw, 210px",
  brand: "180px",
};
